/* ═══════════════════════════════════════════════════════════════
   Timesheets rebuild — manager approval queue (Tranche 2, new UI;
   the underlying Approve/Reject actions are Tranche 0 groundwork).
   Gated on timesheets.approve. Clicking a row jumps the grid to
   that member + week for full-context review before deciding.
   ═══════════════════════════════════════════════════════════════ */

import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Users } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Icon } from '../../components/ui/Icon';

import type { Timesheet, TeamMember } from '../../../shared/types';
import { addDays, formatShort } from './dateUtils';
import { RejectReasonDialog } from './RejectReasonDialog';

export interface ApprovalQueuePanelProps {
  queue: Timesheet[];
  members: TeamMember[];
  onReview: (memberId: string, weekStart: string) => void;
  onApprove: (id: string) => void;
  onReject: (id: string, reason: string) => void;
}

export function ApprovalQueuePanel({
  queue,
  members,
  onReview,
  onApprove,
  onReject,
}: ApprovalQueuePanelProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [rejectTarget, setRejectTarget] = useState<Timesheet | null>(null);

  if (queue.length === 0) return null;

  const memberName = (id: string) => members.find((m) => m.id === id)?.name || 'Unknown';

  return (
    <div
      style={{
        border: '1px solid var(--bd)',
        borderRadius: 'var(--radius-md)',
        background: 'var(--sf)',
        marginBottom: 'var(--space-4)',
        overflow: 'hidden',
      }}
    >
      <button
        onClick={() => setCollapsed((c) => !c)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-2)',
          padding: 'var(--space-3) var(--space-4)',
          background: 'color-mix(in srgb, var(--color-warning) 10%, transparent)',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <Icon icon={Users} em={1.1538} color="var(--color-warning)" />
        <span style={{ fontWeight: 700, fontSize: 'var(--font-size-base)', color: 'var(--tx)' }}>
          {queue.length} timesheet{queue.length === 1 ? '' : 's'} waiting for approval
        </span>
        <span style={{ marginLeft: 'auto' }}>
          {collapsed ? (
            <Icon icon={ChevronDown} em={1.1538} />
          ) : (
            <Icon icon={ChevronUp} em={1.1538} />
          )}
        </span>
      </button>
      {!collapsed && (
        <div>
          {queue.map((ts) => (
            <div
              key={ts.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-3)',
                padding: 'var(--space-2) var(--space-4)',
                borderTop: '1px solid var(--bd)',
                fontSize: 'var(--font-size-base)',
              }}
            >
              <div style={{ flex: 1 }}>
                <span style={{ fontWeight: 600 }}>{memberName(ts.teamMemberId)}</span>{' '}
                <span style={{ color: 'var(--tm)' }}>
                  · {formatShort(ts.weekStart)} – {formatShort(addDays(ts.weekStart, 6))}
                </span>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => onReview(ts.teamMemberId, ts.weekStart)}
              >
                Review
              </Button>
              <Button size="sm" variant="default" onClick={() => onApprove(ts.id)}>
                Approve
              </Button>
              <Button size="sm" variant="destructive" onClick={() => setRejectTarget(ts)}>
                Reject
              </Button>
            </div>
          ))}
        </div>
      )}

      <RejectReasonDialog
        open={!!rejectTarget}
        onOpenChange={(o) => !o && setRejectTarget(null)}
        memberName={rejectTarget ? memberName(rejectTarget.teamMemberId) : undefined}
        onConfirm={(reason) => {
          if (rejectTarget) onReject(rejectTarget.id, reason);
          setRejectTarget(null);
        }}
      />
    </div>
  );
}
